import { supabase } from '../lib/supabase';

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  const converted = {};
  Object.keys(obj)?.forEach(key => {
    const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
    converted[camelKey] = obj?.[key];
  });
  return converted;
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  const converted = {};
  Object.keys(obj)?.forEach(key => {
    const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
    converted[snakeKey] = obj?.[key];
  });
  return converted;
};

export const profileService = {
  // Get current user profile (host or parent)
  async getCurrentProfile() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Try host profile first
    const { data: hostProfile } = await supabase?.from('event_host_profiles')?.select('*')?.eq('id', user?.id)?.single();

    if (hostProfile) {
      return { ...toCamelCase(hostProfile), userType: 'host' };
    }

    // Try parent profile
    const { data: parentProfile } = await supabase?.from('parent_profiles')?.select('*')?.eq('id', user?.id)?.single();

    if (parentProfile) {
      return { ...toCamelCase(parentProfile), userType: 'parent' };
    }

    throw new Error('Profile not found');
  },

  // Update profile information
  async updateProfile(updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const profile = await this.getCurrentProfile();
    const tableName = profile?.userType === 'host' ? 'event_host_profiles' : 'parent_profiles';

    const { data, error } = await supabase?.from(tableName)?.update(toSnakeCase(updates))?.eq('id', user?.id)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  // Get user preferences
  async getPreferences() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_preferences')?.select('*')?.eq('user_id', user?.id)?.single();

    if (error) {
      // Create default preferences if not exist
      const { data: newPrefs } = await supabase?.from('user_preferences')?.insert({ user_id: user?.id })?.select()?.single();
      return toCamelCase(newPrefs);
    }

    return toCamelCase(data);
  },

  // Update preferences
  async updatePreferences(preferences) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_preferences')?.upsert({
        user_id: user?.id,
        ...toSnakeCase(preferences)
      })?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  // Get privacy settings
  async getPrivacy() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_privacy')?.select('*')?.eq('user_id', user?.id)?.single();

    if (error) {
      // Create default privacy if not exist
      const { data: newPrivacy } = await supabase?.from('user_privacy')?.insert({ user_id: user?.id })?.select()?.single();
      return toCamelCase(newPrivacy);
    }

    return toCamelCase(data);
  },

  // Update privacy settings
  async updatePrivacy(privacy) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_privacy')?.upsert({
        user_id: user?.id,
        ...toSnakeCase(privacy)
      })?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  // Get active sessions
  async getSessions() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_sessions')?.select('*')?.eq('user_id', user?.id)?.order('last_active', { ascending: false });

    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  // Delete session
  async deleteSession(sessionId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('user_sessions')?.delete()?.eq('id', sessionId)?.eq('user_id', user?.id);

    if (error) throw error;
  },

  // Change password
  async changePassword(newPassword) {
    const { error } = await supabase?.auth?.updateUser({
      password: newPassword
    });

    if (error) throw error;

    // Log password change
    const { data: { user } } = await supabase?.auth?.getUser();
    if (user) {
      await supabase?.from('password_history')?.insert({
          user_id: user?.id,
          changed_from: 'password_change_form'
        });
    }
  },

  // Upload avatar
  async uploadAvatar(file) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${user?.id}-${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;

    const { error: uploadError } = await supabase?.storage?.from('user-files')?.upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = await supabase?.storage?.from('user-files')?.createSignedUrl(filePath, 31536000); // 1 year

    if (!data?.signedUrl) throw new Error('Failed to get avatar URL');

    return data?.signedUrl;
  },

  // Export user data
  async exportData() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const profile = await this.getCurrentProfile();
    const preferences = await this.getPreferences();
    const privacy = await this.getPrivacy();
    const sessions = await this.getSessions();

    return {
      profile,
      preferences,
      privacy,
      sessions,
      exportedAt: new Date()?.toISOString()
    };
  }
};